import React, { useEffect, useRef, useState } from "react";
import Modal from "../../UI/Modal/Modal";
import Button from "../../UI/Button";
import {
    displayCloseModalDialog,
    displayError,
} from "../../../services/Notifications/NotificationServices";

interface Props {
    file: File;
    onClose: () => void;
    onSave: (file: File) => Promise<void>;
}

type Corner = "nw" | "ne" | "sw" | "se" | null;

const MIN_FRAME_PX = 40;
const HANDLE_RADIUS = 7;

export default function ThumbnailPicker({ file, onClose, onSave }: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const [imgUrl, setImgUrl] = useState<string | null>(null);
    const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
    const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

    const [frame, setFrame] = useState({ x: 0, y: 0, size: 100 });

    const draggingRef = useRef(false);
    const resizingRef = useRef<Corner>(null);
    const dragStartRef = useRef({ mx: 0, my: 0, fx: 0, fy: 0, fsize: 0 });

    const [previewSquare, setPreviewSquare] = useState<string | null>(null);
    const [previewCircle, setPreviewCircle] = useState<string | null>(null);

    useEffect(() => {
        if (onImageLoadTimeoutRef.current) {
            clearTimeout(onImageLoadTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        const url = URL.createObjectURL(file);
        if (file.type.startsWith("video/")) {
            const video = document.createElement("video");
            video.src = url;
            video.muted = true;
            video.playsInline = true;
            video.crossOrigin = "anonymous";

            video.addEventListener(
                "loadeddata",
                () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = video.videoWidth || 640;
                    canvas.height = video.videoHeight || 480;
                    const ctx = canvas.getContext("2d")!;
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const dataUrl = canvas.toDataURL("image/png");
                    setImgUrl(dataUrl);
                    URL.revokeObjectURL(url);
                },
                { once: true },
            );
            video.load();
        } else setImgUrl(url);

        return () => {
            if (imgUrl) URL.revokeObjectURL(imgUrl);
        };
    }, [file]);

    const onImageLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const onImageLoad = (ev: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const img = ev.currentTarget;
        imgRef.current = img;
        const natW = img.naturalWidth;
        const natH = img.naturalHeight;
        setNaturalSize({ w: natW, h: natH });

        const maxW = 540;
        const maxH = 360;
        let dispW = natW;
        let dispH = natH;
        const ratio = natW / natH;

        if (dispW > maxW) {
            dispW = maxW;
            dispH = Math.round(dispW / ratio);
        }
        if (dispH > maxH) {
            dispH = maxH;
            dispW = Math.round(dispH * ratio);
        }
        if (!dispW || !dispH) {
            dispW = Math.min(maxW, 400);
            dispH = Math.min(maxH, 300);
        }

        setDisplaySize({ w: dispW, h: dispH });

        const base = Math.min(dispW, dispH) * 0.6;
        const size = Math.max(MIN_FRAME_PX, Math.round(base));
        const x = Math.round((dispW - size) / 2);
        const y = Math.round((dispH - size) / 2);
        const clamped = clampFrame(x, y, size, dispW, dispH);
        setFrame(clamped);

        if (onImageLoadTimeoutRef.current) {
            clearTimeout(onImageLoadTimeoutRef.current);
        }

        onImageLoadTimeoutRef.current = setTimeout(
            () =>
                makePreviews({
                    x: clamped.x,
                    y: clamped.y,
                    size: clamped.size,
                    dispW,
                    dispH,
                    natW,
                    natH,
                }),
            50,
        );
    };

    const clampFrame = (
        fx: number,
        fy: number,
        fsize: number,
        dispW?: number,
        dispH?: number,
    ) => {
        const w = dispW ?? displaySize.w;
        const h = dispH ?? displaySize.h;

        const margin = HANDLE_RADIUS * 0.01;
        const maxSize = Math.max(
            MIN_FRAME_PX,
            Math.min(w - 2 * margin, h - 2 * margin),
        );

        let nx = fx;
        let ny = fy;
        let ns = fsize;

        if (ns < MIN_FRAME_PX) ns = MIN_FRAME_PX;
        if (ns > maxSize) ns = maxSize;

        if (nx < margin) nx = margin;
        if (ny < margin) ny = margin;
        if (nx + ns > w - margin) nx = Math.max(margin, w - margin - ns);
        if (ny + ns > h - margin) ny = Math.max(margin, h - margin - ns);

        return { x: Math.round(nx), y: Math.round(ny), size: Math.round(ns) };
    };

    useEffect(() => {
        const onMove = (e: PointerEvent) => {
            if (!imgRef.current) return;

            const dispW = displaySize.w;
            const dispH = displaySize.h;
            if (draggingRef.current) {
                const { mx, my, fx, fy } = dragStartRef.current;
                const dx = e.clientX - mx;
                const dy = e.clientY - my;
                const nx = fx + dx;
                const ny = fy + dy;
                const clamped = clampFrame(nx, ny, frame.size, dispW, dispH);

                setFrame(clamped);
                makePreviews({
                    x: clamped.x,
                    y: clamped.y,
                    size: clamped.size,
                    dispW,
                    dispH,
                    natW: naturalSize.w,
                    natH: naturalSize.h,
                });
                return;
            }

            if (resizingRef.current) {
                const corner = resizingRef.current;
                const { mx, my, fx, fy, fsize } = dragStartRef.current;
                const dx = e.clientX - mx;
                const dy = e.clientY - my;
                let nx = fx,
                    ny = fy,
                    ns = fsize;

                if (corner === "se") {
                    const delta = Math.max(dx, dy);
                    ns = Math.max(MIN_FRAME_PX, fsize + delta);
                } else if (corner === "nw") {
                    const delta = Math.max(-dx, -dy);
                    ns = Math.max(MIN_FRAME_PX, fsize + delta);
                    nx = fx + (fsize - ns);
                    ny = fy + (fsize - ns);
                } else if (corner === "ne") {
                    const delta = Math.max(dx, -dy);
                    ns = Math.max(MIN_FRAME_PX, fsize + delta);
                    nx = fx;
                    ny = fy + (fsize - ns);
                } else if (corner === "sw") {
                    const delta = Math.max(-dx, dy);
                    ns = Math.max(MIN_FRAME_PX, fsize + delta);
                    nx = fx + (fsize - ns);
                    ny = fy;
                }

                const cl = clampFrame(nx, ny, ns, dispW, dispH);
                setFrame(cl);
                makePreviews({
                    x: cl.x,
                    y: cl.y,
                    size: cl.size,
                    dispW,
                    dispH,
                    natW: naturalSize.w,
                    natH: naturalSize.h,
                });
            }
        };

        const onUp = () => {
            draggingRef.current = false;
            resizingRef.current = null;
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);

        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
    }, [displaySize, naturalSize, frame.size]);

    const startDrag = (e: React.PointerEvent) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        draggingRef.current = true;
        dragStartRef.current = {
            mx: e.clientX,
            my: e.clientY,
            fx: frame.x,
            fy: frame.y,
            fsize: frame.size,
        };
    };

    const startResize = (corner: Corner) => (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        resizingRef.current = corner;
        dragStartRef.current = {
            mx: e.clientX,
            my: e.clientY,
            fx: frame.x,
            fy: frame.y,
            fsize: frame.size,
        };
    };

    const makePreviews = async (params: {
        x: number;
        y: number;
        size: number;
        dispW: number;
        dispH: number;
        natW: number;
        natH: number;
    }) => {
        if (!imgRef.current) return;
        const { x, y, size, dispW, dispH, natW, natH } = params;
        const scale = natW / dispW;
        const sx = Math.round(x * scale);
        const sy = Math.round(y * scale);
        const sSize = Math.round(size * scale);
        const previewSize = 130;

        const sqCanvas = document.createElement("canvas");
        sqCanvas.width = previewSize;
        sqCanvas.height = previewSize;
        sqCanvas
            .getContext("2d")!
            .drawImage(
                imgRef.current,
                sx,
                sy,
                sSize,
                sSize,
                0,
                0,
                previewSize,
                previewSize,
            );
        setPreviewSquare(sqCanvas.toDataURL("image/png"));

        const circCanvas = document.createElement("canvas");
        circCanvas.width = previewSize;
        circCanvas.height = previewSize;
        const cctx = circCanvas.getContext("2d")!;
        cctx.beginPath();
        const r = previewSize / 2;
        cctx.arc(r, r, r, 0, Math.PI * 2);
        cctx.closePath();
        cctx.clip();
        cctx.drawImage(
            imgRef.current,
            sx,
            sy,
            sSize,
            sSize,
            0,
            0,
            previewSize,
            previewSize,
        );
        setPreviewCircle(circCanvas.toDataURL("image/png"));
    };

    const handleSave = async () => {
        if (!imgRef.current) return;

        const scale = naturalSize.w / displaySize.w;
        const sx = Math.round(frame.x * scale);
        const sy = Math.round(frame.y * scale);
        const sSize = Math.round(frame.size * scale);

        const canvas = document.createElement("canvas");
        canvas.width = sSize;
        canvas.height = sSize;

        canvas
            .getContext("2d")!
            .drawImage(
                imgRef.current,
                sx,
                sy,
                sSize,
                sSize,
                0,
                0,
                sSize,
                sSize,
            );

        canvas.toBlob(async (blob) => {
            if (!blob) return;

            const croppedFile = new File([blob], "avatar_cropped.png", {
                type: blob.type,
            });

            try {
                await onSave(croppedFile);
                onClose();
            } catch (err: any) {
                console.error("BuyForm submit error:", err?.response ?? err);
                const serverMessage =
                    err?.response?.data?.detail ||
                    "Не удалось отправить сообщение";
                displayError(String(serverMessage));
            }
        });
    };

    return (
        <Modal isOpen={true} closeModal={onClose} className="thumbnail-modal">
            <div className="thumbnail-header">
                <h3>Выбор миниатюры</h3>
                <p>
                    Выбранная миниатюра будет использоваться для отображения
                    внутри платформы
                </p>
            </div>

            <div className="thumbnail-body">
                <div className="image-area">
                    <div
                        className="image-inner"
                        style={{
                            width: displaySize.w,
                            height: displaySize.h,
                        }}
                        ref={containerRef}
                    >
                        {imgUrl && (
                            <img
                                src={imgUrl}
                                alt="to-crop"
                                draggable={false}
                                onLoad={onImageLoad}
                                style={{
                                    width: displaySize.w,
                                    height: displaySize.h,
                                }}
                            />
                        )}

                        <div
                            className="frame"
                            style={{
                                left: frame.x,
                                top: frame.y,
                                width: frame.size,
                                height: frame.size,
                            }}
                            onPointerDown={startDrag}
                        >
                            <div
                                className="handle nw"
                                onPointerDown={startResize("nw")}
                            />
                            <div
                                className="handle ne"
                                onPointerDown={startResize("ne")}
                            />
                            <div
                                className="handle sw"
                                onPointerDown={startResize("sw")}
                            />
                            <div
                                className="handle se"
                                onPointerDown={startResize("se")}
                            />
                        </div>
                    </div>
                </div>

                <div className="previews">
                    <div className="preview-circle">
                        {previewCircle ? (
                            <img src={previewCircle} alt="circle preview" />
                        ) : (
                            <div>—</div>
                        )}
                    </div>

                    <div className="preview-square">
                        {previewSquare ? (
                            <img src={previewSquare} alt="square preview" />
                        ) : (
                            <div>—</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="thumbnail-footer">
                <Button
                    refStyle
                    onClick={() =>
                        displayCloseModalDialog(() => {
                            onClose();
                        })
                    }
                >
                    Отмена
                </Button>
                <Button contrastStyle onClick={handleSave}>
                    Сохранить
                </Button>
            </div>
        </Modal>
    );
}
